import React, { useState } from 'react';
import { Experiment, useFeatureFlag, Variant } from '@americanexpress/one-experiments';
import { getActiveLocaleSelector, getIsLoggedInSelector, getTenancySelector } from '@americanexpress/travel-selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { getCookies } from '@americanexpress/one-app-ducks';
import VariantSearchWidgetNavigation from '../SearchWidgetNavigation/VariantSearchWidgetNavigation';
import SearchWidgetNavigation from '../SearchWidgetNavigation/SearchWidgetNavigation';
import { DISABLE_PACKAGES_FF, HOMEPAGE_REDESIGN_EXPERIMENT, VACATION_RENTALS_FF } from '../../constants/oneXpConstants';
import {
  hasCruisesRule, hasNoPackagesRule, hasVacationRentalsRule,
} from '../../constants/featureConstants';
import { useCardIsVREligible } from '../../hooks/useCardIsVREligible';
import { setSelectedTab, setXsTabOpen } from '../../duck';
import {
  CARS_TAB, CRUISES_TAB, FLIGHTS_TAB, GUESTBOOK_TAB, HOTELS_TAB, PACKAGES_TAB, VACATION_RENTALS_TAB,
} from '../../constants/linesOfBusinessConstants';
import { LinesOfBusinessContainer } from '../LineOfBusiness/LinesOfBusinessContainer';
import getTabSelected from '../../selectors/tabSelector';

const SearchWidget = () => {
  const dispatch = useDispatch();
  const tenancy = useSelector(getTenancySelector);
  const locale = useSelector(getActiveLocaleSelector);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const selectedLineOfBusiness = useSelector(getTabSelected);

  const { properties } = useFeatureFlag(
    { featureFlagName: DISABLE_PACKAGES_FF });
  const hasNoPackages = useConditionalFeature(hasNoPackagesRule);
  const isPackageDisabledByFF = properties?.[tenancy]?.includes(locale);

  const { enabled: isVREligibleByFF } = useFeatureFlag(
    { featureFlagName: VACATION_RENTALS_FF });
  const isVREligibleByLocale = useConditionalFeature(hasVacationRentalsRule);
  const isCardVREligible = useCardIsVREligible();
  const isVREligibleByAccount = !isLoggedIn || isCardVREligible;
  const hasVacationRentals = isVREligibleByFF && isVREligibleByLocale && isVREligibleByAccount;

  const hasCruises = useConditionalFeature(hasCruisesRule);

  // todo replace with real FF when available
  const hasGuestbook = useSelector(getCookies)?.tlsguestbook === 'true';

  const enabledLOBs = [
    FLIGHTS_TAB,
    HOTELS_TAB,
    ...!hasGuestbook && hasVacationRentals ? [VACATION_RENTALS_TAB] : [],
    ...hasGuestbook ? [GUESTBOOK_TAB] : [],
    ...!hasNoPackages && !isPackageDisabledByFF ? [PACKAGES_TAB] : [],
    CARS_TAB,
    ...hasCruises ? [CRUISES_TAB] : [],
  ];

  const [isOverflowing, setIsOverflowing] = useState(false);

  const onTabSelected = (selectedTab, shouldOpenXs = false) => {
    if (shouldOpenXs) {
      dispatch(setXsTabOpen(true));
    }
    if (selectedLineOfBusiness !== selectedTab) {
      dispatch(setSelectedTab(selectedTab));
    }
  };

  const variantWidget = (
    <>
      <VariantSearchWidgetNavigation
        displayedLOBs={enabledLOBs}
        onTabSelected={onTabSelected}
      />
      <LinesOfBusinessContainer
        enabledLOBs={enabledLOBs}
        isVariant={true}
      />
    </>
  );

  return (
    <div id="search-widget-container" className="flex flex-column">
      <Experiment
        experimentName={HOMEPAGE_REDESIGN_EXPERIMENT}
        dimensionName="search_widget"
        defaultVariant="control"
      >
        <Variant variant="control">
          <SearchWidgetNavigation
            displayedLOBs={enabledLOBs}
            isOverflowing={isOverflowing}
            setIsOverflowing={setIsOverflowing}
            onTabSelected={onTabSelected}
          />
          <div className="position-relative">
            <LinesOfBusinessContainer
              enabledLOBs={enabledLOBs}
              isOverflowing={isOverflowing}
            />
          </div>
        </Variant>
        <Variant variant="widget_a_with_hero">{variantWidget}</Variant>
        <Variant variant="widget_a_no_hero">{variantWidget}</Variant>
        <Variant variant="widget_b_with_hero">{variantWidget}</Variant>
        <Variant variant="widget_b_no_hero">{variantWidget}</Variant>
      </Experiment>
    </div>
  );
};

export default SearchWidget;
